import Enum from 'enum';

// Mirror --spacing-meeting-footer / --spacing-meeting-header in
// tailwind.config.css. Everything that lays out around the bars reads those CSS
// variables directly; these exist for SenderAloneView, which sizes in JS.
export const FOOTER_HEIGHT = 96;
export const HEADER_HEIGHT = 80;
// Mirrors --spacing-meeting-side-panel (370px) in tailwind.config.css — the
// CSS/JSX side derives the panel width from that token; keep this JS value in sync.
export const RIGHT_PANEL_WIDTH = 370;
export const ONE_PANE_WIDTH_LIMIT = 721;
// How long (ms) an outgoing meeting invite shows as a "calling…" row before it
// auto-expires. Shared by useAddParticipant (rings the invitee, sets the
// expiry) and useMeetingMembers (drops a row for someone who only just left).
export const CALLING_DURATION = 20000;
// Viewport height (CSS px) below which a landscape orientation is treated as
// a phone (right-column layout) rather than a tablet/desktop. iPads in
// landscape have height ≥ 768; phones in landscape are ≤ 430.
export const LANDSCAPE_PHONE_MAX_HEIGHT = 500;
export const LOBBY_VIDEO_WIDTH = 551;
export const LOBBY_VIDEO_HEIGHT = 310;
export const GRID_LIMIT = 9;

export const INTERNATIONAL_CONFERENCE_NUMBER = '+46 770 33 99 88';
export const FREE_PRO_INTERNATIONAL_CONFERENCE_NUMBER = '+33 4 58 58 58 58';
export const FREE_PRO_NATIONAL_CONFERENCE_NUMBER = '+33458585858';

export const OPEN_MEETING_IN_APP_LOCAL_STORAGE_KEY = 'meetingLobbyOpenInApp';

export const uuidRegExp = new RegExp(
    '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-5][0-9a-fA-F]{3}-[089abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$',
);

export const MeetingLocation = new Enum(['LOBBY', 'ACTIVE_MEETING']);

export const WebsocketReadyStates = Object.freeze({
    CONNECTING: 0,
    OPEN: 1,
    CLOSING: 2,
    CLOSED: 3,
});

export const PeerType = Object.freeze({
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
});

export const RoomNotJoinedCause = new Enum(['ROOM_FULL', 'ALREADY_LOGGED_IN']);

// Where this participant stands at the meeting's door (VID-76). Absent — null —
// for anyone with no business at it: every internal participant, everyone the
// server never held back, and every guest once they have been let in.
//
// There is deliberately no ADMITTED. Being admitted is leaving the waiting room
// rather than a place within it, so roomJoined clears the standing instead of
// setting one — which also means null cannot distinguish "never knocked" from
// "knocked and was let in". Nothing needs to; both are simply in the meeting.
//
// The wire keeps its own vocabulary: signaling speaks of admission
// (admissionAware, awaitingApproval, admissionDenied), and AdmissionDeniedCause
// below mirrors it verbatim. This type is the local model, not the protocol.
export const WaitingRoomStanding = Object.freeze({
    WAITING: 'waiting',
    DENIED: 'denied',
    // Removed mid-meeting (VID-471), reusing this slot rather than the door-admission outcomes above.
    REMOVED: 'removed',
});

// How home classifies this participant for a given meeting (VID-76). Values
// match the api-internal MeetingRole enum on the wire. Only EXTERNAL has to ask
// to be let in; a colleague joins straight through whether or not they were
// invited. Whether anyone may join at all is a separate, host-controlled
// setting (see allowNewMembers) — not this.
export const MeetingRole = Object.freeze({
    EXTERNAL: 'EXTERNAL',
    INTERNAL_NONMEMBER: 'INTERNAL_NONMEMBER',
    INTERNAL_MEMBER: 'INTERNAL_MEMBER',
});

// Why a waiting participant was turned away. Values match what signaling puts
// on the wire (uppercase, like RoomNotJoinedCause). DENIED is a deliberate act
// by someone in the meeting; the other two are the server giving up on them.
export const AdmissionDeniedCause = Object.freeze({
    DENIED: 'DENIED',
    TIMED_OUT: 'TIMED_OUT',
    LOBBY_FULL: 'LOBBY_FULL',
});

const ADMISSION_DENIED_MESSAGE_KEY = Object.freeze({
    [AdmissionDeniedCause.DENIED]: 'videoconference.waiting_room.denied',
    [AdmissionDeniedCause.TIMED_OUT]: 'videoconference.waiting_room.timed_out',
    [AdmissionDeniedCause.LOBBY_FULL]:
        'videoconference.waiting_room.lobby_full',
});

// Only a refusal has a heading of its own. Running out of time or out of room
// are not the meeting's answer to you, so they read as one plain line.
const ADMISSION_DENIED_TITLE_KEY = Object.freeze({
    [AdmissionDeniedCause.DENIED]: 'videoconference.waiting_room.access_denied',
});

/**
 * What to tell someone who was turned away from the waiting room. A cause this
 * client does not recognise falls back to the plain denial — they still learn
 * they are not getting in, which is the part that matters.
 */
export const getAdmissionDeniedMessageKey = (cause) =>
    ADMISSION_DENIED_MESSAGE_KEY[cause] ||
    ADMISSION_DENIED_MESSAGE_KEY[AdmissionDeniedCause.DENIED];

/** The heading above that message, or null where the cause has none. */
export const getAdmissionDeniedTitleKey = (cause) =>
    ADMISSION_DENIED_TITLE_KEY[cause] || null;

// How a meeting was created; sent to the backend on creation and mirrors the
// api-internal MeetingType. `.key` is the uppercase name here ('INSTANT'), while
// the API serialises the lowercase value ('instant'), so compare case-insensitively.
// Legacy (pre-AC-1493) meetings report 'none' on read-back — only the two
// values below are ever sent by the client.
export const MeetingType = new Enum(['INSTANT', 'ROOM_FOR_LATER']);

// The host-configured access policy for a meeting (VID-56/VID-307), mirrors
// the api-internal MeetingAccess enum (client-server) 1:1 by name.
export const MeetingAccess = new Enum([
    'ANYONE_WITH_LINK',
    'ANYONE_IN_ORGANISATION',
    'MEMBERS_ONLY',
    'NO_ONE',
]);

// Why a Join attempt would be refused right now (VID-56/VID-307).
export const MeetingJoinBlockedReason = new Enum([
    'NONE',
    'EXTERNAL_NOT_ALLOWED',
    'NOT_A_MEMBER',
    'LOCKED',
]);

export const HARDCODED_CONFERENCE_NUMBER_LIST = [
    {
        countryNameInEnglish: 'Argentina',
        conferenceNumber: '+54 11 5252 6755',
        countryCode: 'ar',
    },
    {
        countryNameInEnglish: 'Canada',
        conferenceNumber: '+1 780 800 4820',
        countryCode: 'ca',
    },
    {
        countryNameInEnglish: 'Cyprus',
        conferenceNumber: '+357 22 232 371',
        countryCode: 'cy',
    },
    {
        countryNameInEnglish: 'Czechia',
        conferenceNumber: '+420 2 2888 0810',
        countryCode: 'cz',
    },
    {
        countryNameInEnglish: 'Dominican Republic',
        conferenceNumber: '+1829 956 6919',
        countryCode: 'do',
    },
    {
        countryNameInEnglish: 'El Salvador',
        conferenceNumber: '+503 2 113 3878',
        countryCode: 'sv',
    },
    {
        countryNameInEnglish: 'Japan',
        conferenceNumber: '+81 3 4578 1782',
        countryCode: 'jp',
    },
    {
        countryNameInEnglish: 'Luxembourg',
        conferenceNumber: '+352 2786 0285',
        countryCode: 'lu',
    },
    {
        countryNameInEnglish: 'Malta',
        conferenceNumber: '+356 2778 0693',
        countryCode: 'mt',
    },
    {
        countryNameInEnglish: 'Mexico',
        conferenceNumber: '+52 554 170 3796',
        countryCode: 'mx',
    },
    {
        countryNameInEnglish: 'New Zealand',
        conferenceNumber: '+64 4 974 7660',
        countryCode: 'nz',
    },
    {
        countryNameInEnglish: 'Panama',
        conferenceNumber: '+507 833 9150',
        countryCode: 'pa',
    },
    {
        countryNameInEnglish: 'Puerto Rico',
        conferenceNumber: '+1787 777 1473',
        countryCode: 'pr',
    },
    {
        countryNameInEnglish: 'Romania',
        conferenceNumber: '+40 31 630 0302',
        countryCode: 'ro',
    },
    {
        countryNameInEnglish: 'Slovakia',
        conferenceNumber: '+421 2 3305 7959',
        countryCode: 'sk',
    },
    {
        countryNameInEnglish: 'Slovenia',
        conferenceNumber: '+386 1 888 8418',
        countryCode: 'si',
    },
    {
        countryNameInEnglish: 'South Africa',
        conferenceNumber: '+27 875 502 185',
        countryCode: 'za',
    },
];
